import React, { createContext, useContext, useMemo } from 'react';
import {
  type GoogleAdsPlacementKey,
  type TenantConfig,
  useGetTenantGoogleAdsPlacements,
  type GoogleAdsPlacement,
  GoogleAdsPlacements,
} from '@/hooks/useTenant';
import {
  getGAMConfigFromTenant,
  deriveDivIdPrefix,
  DEFAULT_PLACEMENT_SIZES,
  type GAMConfig,
} from '@/config/ads.config';
import { isGamMockEnabled, GAM_MOCK_STICKY_FOOTER_PLACEMENT } from '@/dev/gamMock';

interface TenantContextValue {
  tenant: TenantConfig;
  gamConfig: GAMConfig | null;
  adPlacements: GoogleAdsPlacements | null;
  isAdsEnabled: boolean;
  isPlacementEnabled: (key: GoogleAdsPlacementKey) => boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  tenant: TenantConfig;
  children: React.ReactNode;
}

const SQUARES_PLACEMENT_KEYS: GoogleAdsPlacementKey[] = [
  'BODY',
  'STICKY_FOOTER',
  'SQUARES_OVER_LOGO',
  'SQUARES_POST_ENTRY',
];

/** Shape used when a placement carries no sizes and has no per-key default. */
const FOOTER_SHAPED_SIZES: [number, number][] = [
  [320, 50],
  [728, 90],
];

const transformPlacementsToAdPlacements = (
  placements: GoogleAdsPlacement[] | undefined
): GoogleAdsPlacements | null => {
  if (!placements || placements.length === 0) return null;

  const result: Partial<GoogleAdsPlacements> = {};

  placements.forEach(p => {
    const placement = p as GoogleAdsPlacement & { placementKey: GoogleAdsPlacementKey };
    if (!SQUARES_PLACEMENT_KEYS.includes(placement.placementKey)) return;

    const adUnitPath = placement.adUnitPath;
    if (!adUnitPath) return;

    const targeting = placement.targeting
      ? Object.fromEntries(
          Object.entries(placement.targeting).filter(([, v]) => v && v.trim() !== '')
        )
      : undefined;

    const divIdPrefix = deriveDivIdPrefix(placement);
    const defaultSizes = DEFAULT_PLACEMENT_SIZES[placement.placementKey] ?? FOOTER_SHAPED_SIZES;

    result[placement.placementKey] = {
      placementKey: placement.placementKey,
      enabled: placement.enabled,
      adUnitPath,
      sizes: placement.sizes || defaultSizes,
      sizeMappings: placement.sizeMappings,
      divIdPrefix,
      targeting,
    };
  });

  return result as GoogleAdsPlacements;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ tenant, children }) => {
  // `?gamMock=1` forces the ad pipeline on so the whole GAM code path runs locally,
  // where the tenant usually has gamEnabled=false and no placements. When the API
  // does return placements they are used as-is; only a missing STICKY_FOOTER is
  // back-filled with the prod-shaped fixture the footer bug was reported against.
  const gamMockActive = isGamMockEnabled();
  const gamConfig = gamMockActive
    ? { networkId: 'mock-network', enabled: true }
    : getGAMConfigFromTenant(tenant);
  const isAdsEnabled = gamConfig?.enabled || false;
  const { data: fetchedPlacements } = useGetTenantGoogleAdsPlacements(tenant.code);

  const placements = useMemo(() => {
    if (!gamMockActive) return fetchedPlacements;
    const list = fetchedPlacements ?? [];
    // A STICKY_FOOTER row that could never serve (disabled, or no ad unit path —
    // the local seed data ships one) is replaced, not kept: the point of the mock
    // is that the footer slot always fills locally.
    const usable = list.filter(
      p => p.placementKey !== 'STICKY_FOOTER' || (p.enabled && p.adUnitPath)
    );
    return usable.some(p => p.placementKey === 'STICKY_FOOTER')
      ? usable
      : [...usable, GAM_MOCK_STICKY_FOOTER_PLACEMENT];
  }, [fetchedPlacements, gamMockActive]);

  const adPlacements = useMemo(() => {
    return transformPlacementsToAdPlacements(placements);
  }, [placements]);

  const enabledMap = new Map<string, boolean>(
    (placements || []).map(p => [p.placementKey, p.enabled])
  );
  const isPlacementEnabled = (key: GoogleAdsPlacementKey) => enabledMap.get(key) === true;

  return (
    <TenantContext.Provider
      value={{
        tenant,
        gamConfig,
        adPlacements,
        isAdsEnabled,
        isPlacementEnabled,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
